import { BaseService } from './base';

export interface ProductImage {
  url: string;
  alt: string;
  position: number;
}

export interface Product {
  id: string;
  store_id: string;
  magento_id: string;
  sku: string;
  name: string;
  main_image_url?: string | null;
  gallery_images?: any[] | null;
  category?: string | null;
  status?: string | null;
  stock_quantity?: number | null;
  stock_source_code?: string | null;
  last_synced?: string | null;
  created_at: string;
  // Transformation tracking
  transformation_status?: 'untransformed' | 'partial' | 'complete' | null;
  applied_transformations?: string[] | null;
  last_transformed_at?: string | null;
  stores?: {
    id: string;
    name: string;
    url: string;
  };
}

export interface TransformationStats {
  total: number;
  untransformed: number;
  partial: number;
  complete: number;
}

export interface BulkSelectResponse {
  selected_ids: string[];
  count: number;
  category: string;
  stats: TransformationStats;
  remaining: number;
  limit_applied: boolean;
  message: string;
}

export interface ProductsResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface SyncProductsResponse {
  message: string;
  synced_count: number;
  products: Product[];
}

export interface ProductFilters {
  store_id?: string;
  search?: string;
  category?: string;
  has_images?: boolean;
  stock_status?: 'in_stock' | 'out_of_stock';
  page?: number;
  limit?: number;
}

export interface ProductImageCounts {
  [productId: string]: number;
}

export interface ProductImageCountsResponse {
  image_counts: ProductImageCounts;
  total_images: number;
  total_main_images: number;
  total_gallery_images: number;
  products_queried: number;
  products_found: number;
}

export interface ProductImageCountsRequest {
  product_ids: string[];
}

export interface CheckStockResponse {
  stock_quantity: number | null;
  stock_source_code: string;
  stock_status: 'in_stock' | 'out_of_stock' | 'not_found' | 'unknown';
  sku: string;
}

class ProductService extends BaseService {
  private endpoint = '/products';

  async getProducts(filters: ProductFilters = {}): Promise<ProductsResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const url = queryString ? `${this.endpoint}?${queryString}` : this.endpoint;
    
    // Use makeFullRequest to get pagination data
    const response = await this.makeFullRequest<Product[]>(url);
    
    return {
      products: response.data || [],
      pagination: response.pagination || {
        page: 1,
        limit: 50,
        total: 0,
        pages: 0
      }
    };
  }

  async getProduct(id: string): Promise<Product> {
    const response = await this.makeRequest<{ product: Product }>(`${this.endpoint}/${id}`);
    return response.product;
  }

  async syncProducts(storeId: string): Promise<SyncProductsResponse> {
    return this.makeRequest<SyncProductsResponse>(`${this.endpoint}/sync/${storeId}`, {
      method: 'POST',
    });
  }

  async getCategories(storeId?: string): Promise<string[]> {
    const url = storeId 
      ? `${this.endpoint}/categories/list?store_id=${storeId}`
      : `${this.endpoint}/categories/list`;
    const response = await this.makeRequest<{ categories: string[] }>(url);
    return response.categories;
  }

  async bulkSelectByCategory(
    category: string,
    options: { store_id?: string; include_transformed?: boolean } = {}
  ): Promise<BulkSelectResponse> {
    const response = await this.makeRequest<BulkSelectResponse>(`${this.endpoint}/bulk-select-by-category`, {
      method: 'POST',
      body: JSON.stringify({
        category,
        store_id: options.store_id,
        include_transformed: options.include_transformed ?? false
      }),
    });
    return response;
  }

  async getCategoryTransformationStats(category: string, storeId?: string): Promise<{ category: string; stats: TransformationStats }> {
    const url = storeId
      ? `${this.endpoint}/categories/${encodeURIComponent(category)}/transformation-stats?store_id=${storeId}`
      : `${this.endpoint}/categories/${encodeURIComponent(category)}/transformation-stats`;
    return this.makeRequest(url);
  }

  async resetProductTransformations(productId: string): Promise<{ product_id: string; message: string }> {
    return this.makeRequest(`${this.endpoint}/${productId}/reset-transformations`, {
      method: 'POST',
    });
  }

  async rollbackProductTransformations(productId: string): Promise<{ product_id: string; message: string; rollback_count: number }> {
    return this.makeRequest(`${this.endpoint}/${productId}/rollback-transformations`, {
      method: 'POST',
    });
  }

  async resetBulkTransformations(productIds: string[]): Promise<{ requested: number; reset: number; failed: number }> {
    return this.makeRequest(`${this.endpoint}/bulk-reset-transformations`, {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds }),
    });
  }

  async resetCategoryTransformations(category: string, storeId?: string): Promise<{ category: string; reset: number; message: string }> {
    return this.makeRequest(`${this.endpoint}/reset-category-transformations`, {
      method: 'POST',
      body: JSON.stringify({ category, store_id: storeId }),
    });
  }

  async getBulkProducts(productIds: string[], page: number = 1, limit: number = 50, search?: string): Promise<ProductsResponse> {
    const body = {
      ids: productIds,
      page,
      limit,
      ...(search && { search }),
    };

    // Use makeFullRequest to get pagination data
    const response = await this.makeFullRequest<Product[]>(`${this.endpoint}/bulk`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    
    return {
      products: response.data || [],
      pagination: response.pagination || {
        page,
        limit,
        total: 0,
        pages: 0
      }
    };
  }

  /**
   * Get actual image counts for specified products
   * This replaces the inaccurate "3 images per product" assumption
   */
  async getImageCounts(productIds: string[]): Promise<ProductImageCountsResponse> {
    const response = await this.makeRequest<{
      success?: boolean;
      data?: ProductImageCountsResponse;
      image_counts?: ProductImageCounts;
      total_images?: number;
      total_main_images?: number;
      total_gallery_images?: number;
      products_queried?: number;
      products_found?: number;
    }>(`${this.endpoint}/image-counts`, {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds }),
    });

    // Handle both response formats for backward compatibility
    if (response.data) {
      return response.data;
    } else {
      return {
        image_counts: response.image_counts || {},
        total_images: response.total_images || 0,
        total_main_images: response.total_main_images || 0,
        total_gallery_images: response.total_gallery_images || 0,
        products_queried: response.products_queried || 0,
        products_found: response.products_found || 0,
      };
    }
  }

  /**
   * Get total image count for specified products
   * Convenience method that returns just the total
   */
  async getTotalImageCount(productIds: string[]): Promise<number> {
    try {
      const response = await this.getImageCounts(productIds);
      return response.total_images;
    } catch (error) {
      console.error('Failed to get total image count, using fallback:', error);
      // Fallback: assume 2 images per product (reduced from 3 based on analysis)
      return productIds.length * 2;
    }
  }

  /**
   * Get image count for a single product
   */
  async getProductImageCount(productId: string): Promise<number> {
    try {
      const response = await this.getImageCounts([productId]);
      return response.image_counts[productId] || 1;
    } catch (error) {
      console.error('Failed to get product image count, using fallback:', error);
      return 1;
    }
  }

  /**
   * Check stock for a single product and update database
   */
  async checkStock(productId: string, inventorySourceCode: string = 'online_wh'): Promise<CheckStockResponse> {
    const response = await this.makeRequest<CheckStockResponse>('/utils/check-product-stock', {
      method: 'POST',
      body: JSON.stringify({
        product_id: productId,
        inventory_source_code: inventorySourceCode
      }),
    });
    return response;
  }
}

export const productService = new ProductService();